import type * as L from 'leaflet';
// Type-only: brings @types/leaflet.markercluster's `declare module 'leaflet'`
// augmentation (L.markerClusterGroup, L.MarkerClusterGroup) into scope
// without a runtime import.
import type {} from 'leaflet.markercluster';

export type LeafletModule = typeof L;

let cached: Promise<LeafletModule> | null = null;

/**
 * Loads Leaflet + the leaflet.markercluster plugin exactly once per page
 * session and hands back the same object to every caller.
 *
 * leaflet and leaflet.markercluster are CJS/UMD packages that interop
 * through globals rather than module exchange: leaflet's bundle ends with
 * `window.L = exports`, and leaflet.markercluster never imports leaflet at
 * all — it patches the free global `L` directly. If more than one physical
 * copy of leaflet is ever bundled (easy to do across independently
 * lazy-loaded routes mixing static and dynamic `import('leaflet')`), the
 * plugin's mutation can land on a different object than the one a given
 * component ends up holding, producing "L.markerClusterGroup is not a
 * function". Pinning globalThis.L to the exact resolved module before
 * loading the plugin guarantees the mutation always lands on the object
 * this function returns, regardless of how the bundler split things up.
 */
export function loadLeaflet(): Promise<LeafletModule> {
  if (!cached) {
    cached = (async () => {
      const mod = await import('leaflet');
      const leaflet = ((mod as unknown as { default?: LeafletModule }).default ?? mod) as LeafletModule;

      (globalThis as unknown as { L: LeafletModule }).L = leaflet;
      if (typeof window !== 'undefined') {
        (window as unknown as { L: LeafletModule }).L = leaflet;
      }

      await import('leaflet.markercluster');

      if (typeof (leaflet as unknown as { markerClusterGroup?: unknown }).markerClusterGroup !== 'function') {
        throw new Error('leaflet.markercluster failed to attach to the Leaflet instance');
      }

      return leaflet;
    })().catch((err) => {
      cached = null;
      throw err;
    });
  }
  return cached;
}
